import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data/site";
import { Logo, FlagPair } from "./Brand";
import { usePages } from "../context/PagesContext";

export const Navbar = ({ solid = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const pages = usePages();
  const navPages = pages.filter((p) => p.show_in_nav);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const opaque = scrolled || solid;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background,box-shadow,border] duration-300 ${
        opaque ? "bg-white/85 backdrop-blur-xl border-b border-gray-200 shadow-sm" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Logo light={!opaque} href="/" />

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                opaque ? "text-navy hover:text-brandred" : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
          {navPages.map((p) => (
            <a
              key={p.id}
              href={`/${p.path}`}
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                opaque ? "text-navy hover:text-brandred" : "text-white/90 hover:text-white"
              }`}
            >
              {p.title}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <FlagPair className={opaque ? "text-navy" : "text-white"} />
          <a
            href="/#quote"
            data-testid="nav-quote-btn"
            className="rounded-lg bg-brandred px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-brandred/30 transition-transform hover:bg-brandred-700 active:scale-95"
          >
            Get a Quote
          </a>
        </div>

        <button
          className={`lg:hidden ${opaque ? "text-navy" : "text-white"}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-gray-200 bg-white lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1 px-4 py-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-base font-semibold text-navy hover:bg-navy-50"
              >
                {l.label}
              </a>
            ))}
            {navPages.map((p) => (
              <a
                key={p.id}
                href={`/${p.path}`}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-base font-semibold text-navy hover:bg-navy-50"
              >
                {p.title}
              </a>
            ))}
            <a
              href="/#quote"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-lg bg-brandred px-5 py-3 text-center text-base font-bold text-white"
            >
              Get a Quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
